import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {Box} from '@amblique/base/app/components/shared/ui';
import {Component} from '@amblique/king-living/app/components/contentful/component';
import {Swiper} from '@amblique/king-living/app/components/shared/swiper';

const SWIPE_MIN_PX = 50;
const AUTOPLAY_MS = 5000;

/** `clip-path` is animated; set via ref — not React `style` (re-renders would overwrite). */
const CLIP_PATH_TRANSITION = 'clip-path 0.55s cubic-bezier(0.4, 0, 0.2, 1)';
const CLIP_FULLY_HIDDEN = 'inset(0 0 0 100%)';

function setClipPathCompat(topEl, path) {
  topEl.style.clipPath = path;
  if (path) {
    topEl.style.setProperty('-webkit-clip-path', path);
  } else {
    topEl.style.removeProperty('-webkit-clip-path');
  }
}

function setTopLayerClipInstant(topEl, path) {
  if (!topEl) return;
  topEl.style.transition = 'none';
  setClipPathCompat(topEl, path);
}

function runTopLayerCurtainAnim(topEl, forward) {
  if (!topEl) return;
  topEl.style.transition = 'none';
  if (forward) {
    setClipPathCompat(topEl, 'inset(0 0 0 0%)');
  } else {
    setClipPathCompat(topEl, 'inset(0 0% 0 0)');
  }
  void topEl.offsetWidth;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      topEl.style.transition = CLIP_PATH_TRANSITION;
      if (forward) {
        setClipPathCompat(topEl, 'inset(0 0 0 100%)');
      } else {
        setClipPathCompat(topEl, 'inset(0 100% 0 0)');
      }
    });
  });
}

/**
 * Curtain-style slide change (clip-path) with optional thumbnails, swipe, and autoplay.
 * Thumbs: same Swiper theme / options as Media Carousel `CF-013-Thumbs`.
 */
export const Cf013CurtainView = ({
  mainItems,
  thumbImageEntries = [],
  autoPlay = false,
  stylesRoot = {},
  ...rest
}) => {
  const slides = mainItems?.length ? mainItems : [];
  const n = slides.length;
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const thumbsSwiperRef = useRef(null);

  const viewRef = useRef(null);
  const topWrapRef = useRef(null);
  const backImgRef = useRef(null);
  const topImgRef = useRef(null);
  const busyRef = useRef(false);
  const pendingToRef = useRef(0);
  const indexRef = useRef(0);
  const goSlideRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(0);

  const onThumbsSwiper = useCallback((s) => {
    thumbsSwiperRef.current = s;
    setThumbsSwiper(s);
  }, []);

  const setSrcDecode = (img, src) => {
    if (!img || !src) return;
    img.src = src;
    if (img.decode) {
      img.decode().catch(() => {});
    }
  };

  const finishPendingSlide = useCallback(() => {
    const to = pendingToRef.current;

    indexRef.current = to;
    setActiveIndex(to);
    const next = slides[to];

    if (!next) {
      busyRef.current = false;

      return;
    }
    setSrcDecode(backImgRef.current, next.src);
    setSrcDecode(topImgRef.current, next.src);
    if (backImgRef.current) {
      backImgRef.current.alt = next.alt || '';
    }
    if (topWrapRef.current) {
      setTopLayerClipInstant(topWrapRef.current, CLIP_FULLY_HIDDEN);
    }
    busyRef.current = false;
  }, [slides]);

  /**
   * Same curtain clip-path as next/prev: `forward` = vertical edge sweeps to the right.
   * @param {number} to
   * @param {boolean} forward
   */
  const playCurtainTo = useCallback(
    (to, forward, {syncThumbWithMain = false} = {}) => {
      if (n < 2 || busyRef.current) return;
      const from = indexRef.current;

      if (to < 0 || to >= n || to === from) return;

      const incoming = slides[to];
      const outgoing = slides[from];

      if (!incoming || !outgoing) {
        return;
      }
      busyRef.current = true;
      pendingToRef.current = to;
      if (syncThumbWithMain) {
        indexRef.current = to;
        setActiveIndex(to);
      }
      setSrcDecode(backImgRef.current, incoming.src);
      setSrcDecode(topImgRef.current, outgoing.src);
      if (backImgRef.current) {
        backImgRef.current.alt = incoming.alt || '';
      }
      const topEl = topWrapRef.current;

      runTopLayerCurtainAnim(topEl, forward);
    },
    [n, slides]
  );

  const goSlide = useCallback(
    (dir) => {
      if (n < 2) return;
      const from = indexRef.current;
      const to = (from + dir + n) % n;

      if (to === from) return;
      const forward = dir > 0;

      playCurtainTo(to, forward);
    },
    [n, playCurtainTo]
  );

  useEffect(() => {
    goSlideRef.current = goSlide;
  }, [goSlide]);

  useLayoutEffect(() => {
    const topEl = topWrapRef.current;

    if (!topEl) {
      return undefined;
    }
    const onTransitionEnd = (e) => {
      if (e.target !== topEl) return;
      const p = e.propertyName || '';

      if (!/clip|clip-path/i.test(p) && p !== 'webkit-clip-path') {
        return;
      }
      if (!busyRef.current) return;
      finishPendingSlide();
    };

    topEl.addEventListener('transitionend', onTransitionEnd);

    return () => {
      topEl.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [finishPendingSlide, n]);

  const slideIds = (mainItems ?? []).map((s) => s.id).join();

  useLayoutEffect(() => {
    const list = mainItems ?? [];

    if (list.length < 1) return;
    const first = list[0];

    setSrcDecode(backImgRef.current, first.src);
    setSrcDecode(topImgRef.current, first.src);
    if (backImgRef.current) {
      backImgRef.current.alt = first.alt || '';
    }
    if (topWrapRef.current) {
      setTopLayerClipInstant(topWrapRef.current, CLIP_FULLY_HIDDEN);
    }
    indexRef.current = 0;
    setActiveIndex(0);
  }, [mainItems, n, slideIds]);

  useEffect(() => {
    if (!autoPlay || n < 2) return undefined;
    const id = setInterval(() => {
      goSlideRef.current?.(1);
    }, AUTOPLAY_MS);

    return () => {
      clearInterval(id);
    };
  }, [autoPlay, n]);

  /**
   * Thumbnail click: one curtain transition, direction = shorter arc on the slide ring.
   */
  const goToIndexFromThumb = useCallback(
    (to) => {
      if (n < 2) return;
      if (to < 0 || to >= n) return;
      if (to === indexRef.current) return;
      if (busyRef.current) return;

      const from = indexRef.current;
      const delta = (to - from + n) % n;

      if (delta === 0) return;
      const forward = delta * 2 < n;

      playCurtainTo(to, forward, {syncThumbWithMain: true});
    },
    [n, playCurtainTo]
  );

  // Align thumb `Swiper` to `activeIndex` (avoid `s.update()` after `slideTo` — it can reset with `centeredSlides`).
  useLayoutEffect(() => {
    const s = thumbsSwiperRef.current;

    if (!s || s.destroyed || n < 1) return;
    const slideCount = s.slides?.length ?? 0;

    if (slideCount < 1) return;
    const target = Math.min(Math.max(0, activeIndex), slideCount - 1, n - 1);

    if (s.activeIndex !== target) s.slideTo(target, 0, true);
  }, [thumbsSwiper, activeIndex, n]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goSlide(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goSlide(1);
      }
    },
    [goSlide]
  );

  useEffect(() => {
    const el = viewRef.current;

    if (!el) return undefined;
    let swipeX0 = 0;
    let swipeY0 = 0;
    let swipeActive = false;
    let swipePid = 0;

    const endSwipe = (e) => {
      if (e && e.pointerId !== swipePid) return;
      if (!swipeActive) return;
      swipeActive = false;
      try {
        if (e && e.pointerId != null) {
          el.releasePointerCapture(e.pointerId);
        }
      } catch {
        /* not capturing */
      }
      if (busyRef.current) return;
      if (!e) return;
      const dx = e.clientX - swipeX0;
      const dy = e.clientY - swipeY0;

      if (Math.abs(dx) < SWIPE_MIN_PX) return;
      if (Math.abs(dy) > Math.abs(dx)) return;
      if (dx < 0) {
        goSlide(1);
      } else {
        goSlide(-1);
      }
    };

    const onDown = (e) => {
      if (busyRef.current) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      swipeActive = true;
      swipePid = e.pointerId;
      swipeX0 = e.clientX;
      swipeY0 = e.clientY;
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* */
      }
      const onEnd = (pe) => {
        if (pe.pointerId !== swipePid) return;
        document.removeEventListener('pointerup', onEnd, true);
        document.removeEventListener('pointercancel', onEnd, true);
        if (pe.type === 'pointercancel') {
          try {
            el.releasePointerCapture(swipePid);
          } catch {
            /* */
          }
          swipeActive = false;

          return;
        }
        endSwipe(pe);
      };

      document.addEventListener('pointerup', onEnd, true);
      document.addEventListener('pointercancel', onEnd, true);
    };

    el.addEventListener('pointerdown', onDown);

    return () => {
      el.removeEventListener('pointerdown', onDown);
    };
  }, [goSlide]);

  if (n < 1) {
    return null;
  }

  return (
    <Box {...stylesRoot?.root} {...rest}>
      <Box position="relative" w="100%" zIndex={0}>
        <Box
          ref={viewRef}
          aria-label="Image carousel"
          className="cf013-curtain"
          role="group"
          tabIndex={0}
          sx={{
            aspectRatio: '16/9',
            backgroundColor: 'neutral.200',
            borderRadius: 'md',
            maxW: '100%',
            outline: 'none',
            overflow: 'hidden',
            position: 'relative',
            touchAction: 'none',
            userSelect: 'none',
            w: '100%',
          }}
          onKeyDown={onKeyDown}
        >
          <Box
            className="cf013-curtain__back"
            left={0}
            pointerEvents="none"
            position="absolute"
            sx={{inset: 0, zIndex: 0}}
            top={0}
          >
            <Box
              ref={backImgRef}
              alt=""
              as="img"
              decoding="async"
              display="block"
              h="100%"
              objectFit="cover"
              w="100%"
            />
          </Box>
          <Box
            ref={topWrapRef}
            className="cf013-curtain__top"
            left={0}
            pointerEvents="none"
            position="absolute"
            sx={{inset: 0, zIndex: 1, overflow: 'hidden'}}
            top={0}
            aria-hidden
          >
            <Box
              ref={topImgRef}
              alt=""
              as="img"
              decoding="async"
              display="block"
              h="100%"
              objectFit="cover"
              w="100%"
            />
          </Box>
        </Box>

        {Array.isArray(thumbImageEntries) && thumbImageEntries.length > 0 && (
          <Swiper
            options={{autoplay: false, slideToClickedSlide: false}}
            presets="CF-013-Thumbs"
            sx={{pointerEvents: 'auto', zIndex: 2}}
            thumbTabListLabel="Image carousel thumbnails"
            variant="CF-013-Thumbs"
            onSwiper={onThumbsSwiper}
            onThumbActivate={goToIndexFromThumb}
            thumbActivateOnClick
          >
            {thumbImageEntries.map((thumb) => (
              <Component key={thumb.sys.id} component={thumb} isComplete />
            ))}
          </Swiper>
        )}
      </Box>
    </Box>
  );
};

Cf013CurtainView.displayName = 'Cf013CurtainView';

Cf013CurtainView.propTypes = {
  mainItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      src: PropTypes.string.isRequired,
      alt: PropTypes.string,
    })
  ),
  autoPlay: PropTypes.bool,
  stylesRoot: PropTypes.object,

  /** Contentful `Image` entries, same count/order as `mainItems`. */
  thumbImageEntries: PropTypes.arrayOf(PropTypes.object),
};
